
import { Injectable, OnDestroy, inject } from "@angular/core";
import _ from "lodash";
import { BehaviorSubject, Observable, Subject, firstValueFrom } from "rxjs";
import { concatMap, filter, takeUntil, tap } from "rxjs/operators";
import { ApiClientService } from "~app/common/services/api-client.service";
import { CommandService, ICommand } from "~app/common/services/cqs/command.service";
import { GuidUtils } from "~app/common/utils/guid-utils";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { UpdateAtgardKedjaCommand } from "../commands/kedjakoppling/UpdateAtgardKedjaCommand";
import { VKAtgardKedja } from "../models/Atgardskedjor/atgardskedja";
import {
    IVKAtgardKedjaInfoResponseDTO,
    IVKAtgardKedjaKoppling,
    IVKAtgardKedjaKopplingMasshanteringsResponseDTO,
    IVKAtgardKedjaKopplingResponseDTO,
    mapKedjaKopplingFromResponseDTO
} from "../models/Atgardskedjor/atgardskedjor-models";
import { Atgard, SkogsvardEntity } from "../models/atgard-models";
import { Avtal } from "../models/avtal-models";
import { AvtalMediatorService } from "./avtal-mediator.service";
import { KontraktCommandService } from "./kontrakt-command.service";
import { KontraktMediatorService } from "./kontrakt-mediator.service";

@Injectable()
export class AtgardKedjaMediatorService implements OnDestroy {
    private readonly api = inject(ApiClientService);
    private readonly avtalMediatorService = inject(AvtalMediatorService);
    private readonly commandService = inject(CommandService);
    private readonly kontraktMediatorService = inject(KontraktMediatorService);
    private readonly kontraktCommandService = inject(KontraktCommandService);

    private virkeskopAvtal: Avtal;
    private readonly kontraktKedjorSrc = new BehaviorSubject<boolean>(false);
    private readonly kontraktsKedjor = new Map<string, VKAtgardKedja>();
    private readonly ngUnsubscribe$ = new Subject<void>();

    private readonly externalKedjaKopplingQueue$ = new Subject<ICreateExternalKedjeKoppling>();

    private orginalKedjor = new Map<string, VKAtgardKedja>();

    get kontraktKedjor$() {
        return this.kontraktKedjorSrc.asObservable();
    }

    constructor() {

        // Exekverar observable-strömmen sekvensiellt oavsett antalet objekt i kön
        // Det måste göras i fallet när man skapar nya skogsvårdsåtgärder och vill koppla
        // dessa till en extern åtgärd (som alltså finns i ett annat avtal)
        this.externalKedjaKopplingQueue$.pipe(
            takeUntil(this.ngUnsubscribe$),
            concatMap((queuedItem) => this.createAtgardKedjakopplingForExternalAvtalAtgard(queuedItem.parentAtgard, queuedItem.childAtgard))
        ).subscribe();

        this.avtalMediatorService.avtal$.pipe(
            takeUntil(this.ngUnsubscribe$)
        ).subscribe(co => {
            this.virkeskopAvtal = co?.current;

            if (this.virkeskopAvtal?.id) {
                this.reloadAvtalsKedjor();
            } else {
                this.kontraktsKedjor.clear();
            }
        });

        //När en åtgärd tagits bort så ska den försvinna ur kedjan.
        this.kontraktMediatorService.atgardRemoved$
            .pipe(takeUntil(this.ngUnsubscribe$), filter(a => ObjectUtils.hasValue(a)))
            .subscribe(atgard => this.removeAtgardKedjakoppling(atgard));

    }

    ngOnDestroy(): void {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    enqueueExternalKedjaCheck(atgardParent: Atgard, atgardChild: Atgard) {
        this.externalKedjaKopplingQueue$.next({ parentAtgard: atgardParent,
            childAtgard: atgardChild });
    }

    createAtgardKedjakoppling(atgardParent: Atgard, atgardChild: Atgard): void {

        //Hitta kedjansGuid från atgardParent Guid
        let kedja = this.getKedjaByAtgardGuid(atgardParent.guid);
        if (!kedja) {
            //Skapar en ny kedja
            const kedjaGuid = GuidUtils.newGuid();
            kedja = new VKAtgardKedja(kedjaGuid);
            this.kontraktsKedjor.set(kedjaGuid, kedja);
            kedja.createNewKoppling(this.avtalMediatorService.avtal.id, this.kontraktMediatorService.kontrakt.id, atgardParent);
        }
        kedja.createNewKoppling(this.avtalMediatorService.avtal.id, this.kontraktMediatorService.kontrakt.id, atgardChild);
        this.updateKedjaCommand(kedja);
    }

    getAtgardKedjaKopplingarByAtgard(atgardGuid: string): IVKAtgardKedjaKoppling[] {
        const kedja = this.getKedjaByAtgardGuid(atgardGuid);
        if (!kedja) {
            return null;
        }

        return kedja.getDisplayKopplingar();
    }

    getSorteradAtgardslista(atgarder: SkogsvardEntity[]): SkogsvardEntity[] {
        this.kontraktsKedjor.forEach((kedja, kedjeGuid) => {
            const atgardIds = kedja.kedjaKopplingar.filter(kk => kk.atgardId).map(kk => kk.atgardId);
            const lowestAtgardId = Math.min(...atgardIds);
            kedja.kedjaKopplingar.forEach((koppling) => {
                const anknytenAtgard = atgarder.find(atgard => atgard.guid === koppling.atgardGuid);
                if(anknytenAtgard) {
                    anknytenAtgard.lopnummerKedja = lowestAtgardId;
                    anknytenAtgard.lopnummer = koppling.lopnummer;
                }
            });
        });
        atgarder.forEach(atg => {
            if(!atg.lopnummerKedja) {
                atg.lopnummerKedja = atg.id ? atg.id + 1000000000 : Number.MAX_SAFE_INTEGER;
            }
        });

        return _.orderBy(atgarder, [
            "lopnummerKedja",
            "lopnummer",
            "atgardId"
        ]);
    }

    getMasshanteraKedjaKopplingarByKedjaGuid(kedjaGuid: string): Observable<IVKAtgardKedjaKopplingMasshanteringsResponseDTO[]> {
        return this.getKedjaKopplingarWithKedjaGuid(kedjaGuid);
    }

    saveMassEditeringsKedja(kopplingar: IVKAtgardKedjaKopplingMasshanteringsResponseDTO[]): Observable<void> {
        const kedja = VKAtgardKedja.createKedjaFromMasshantering(kopplingar);

        const command = new UpdateAtgardKedjaCommand(
            kedja.kedjaRowVersion,
            this.kontraktMediatorService.kontrakt.guid,
            kedja.kedjaGuid,
            kedja.kedjaKopplingar
        );
        return this.executeCommand(command);
    }

    reloadAvtalsKedjor() {
        this.kontraktsKedjor.clear();
        this.getAvtalKontraktsAtgardKedjor(this.virkeskopAvtal.id).subscribe(kedjaDb => {
            kedjaDb?.forEach(koppling => {
                this.generateAtgardsKedja(koppling);
            });
            this.orginalKedjor = _.cloneDeep(this.kontraktsKedjor);
            this.kontraktKedjorSrc.next(true);
        });
    }

    updateAtgardskedjaForAtgard(atgard: Atgard): void {
        const kedja = this.getKedjaByAtgardGuid(atgard.guid);
        const koppling = kedja.kedjaKopplingar.find(x => x.atgardGuid === atgard.guid);
        koppling.atgardstyp = atgard.atgardstyp;

        kedja.reorderKopplingsKedja();
        this.updateKedjaCommand(kedja);
    }

    private async createAtgardKedjakopplingForExternalAvtalAtgard(atgardParent: Atgard, atgardChild: Atgard): Promise<void> {
        //Hitta kedjansGuid från atgardParent Guid
        const existingKedja = this.getKedjaByAtgardGuid(atgardParent.guid);

        if (existingKedja) {
            this.createAtgardKedjakoppling(atgardParent, atgardChild);
            return;
        }

        // Slår upp den externa åtgärdens fulla kedja
        const atgardKedjeInfo = await firstValueFrom(this.getAtgardKedjaInfoForAtgard(atgardParent.id));

        // Om inga kopplingar existerar så skapar vi en ny kedja och lägger till kopplingen
        if (atgardKedjeInfo.kopplingar.length === 0) {
            //Skapar en ny kedja
            const kedjaGuid = GuidUtils.newGuid();
            const kedja = new VKAtgardKedja(kedjaGuid);
            this.kontraktsKedjor.set(kedjaGuid, kedja);
            kedja.createNewKoppling(this.avtalMediatorService.avtal.id, this.kontraktMediatorService.kontrakt.id, atgardParent);
            kedja.createNewKoppling(this.avtalMediatorService.avtal.id, this.kontraktMediatorService.kontrakt.id, atgardChild);
            this.updateKedjaCommand(kedja);
        } else {
            // Extern kedja existerar, mappa upp det och lägg till ny koppling
            atgardKedjeInfo.kopplingar.forEach(koppling => this.generateAtgardsKedja(koppling));
            const externKedja = this.getKedjaByAtgardGuid(atgardParent.guid);
            externKedja.createNewKoppling(this.avtalMediatorService.avtal.id, this.kontraktMediatorService.kontrakt.id, atgardChild);
            this.updateKedjaCommand(externKedja);
        }
    }

    private generateAtgardsKedja(koppling: IVKAtgardKedjaKopplingResponseDTO) {
        if (!this.kontraktsKedjor.has(koppling.kedjaGuid)) {
            const kedja = new VKAtgardKedja(koppling.kedjaGuid);
            this.kontraktsKedjor.set(koppling.kedjaGuid, kedja);
        }
        this.kontraktsKedjor.get(koppling.kedjaGuid).addExistingKedjakoppling(mapKedjaKopplingFromResponseDTO(koppling));
    }

    private removeAtgardKedjakoppling(atgard: Atgard): void {
        //Hitta kedjansGuid från atgard guid
        const kedja = this.getKedjaByAtgardGuid(atgard.guid);
        if (!kedja) {
            return;
        }
        // Om kopplingen inte fanns i kedjan från början, Commandet tas bort
        const kopplingExistsInOrginalKedja = this.getKedjaByAtgardGuid(atgard.guid, this.orginalKedjor.values());
        if(!kopplingExistsInOrginalKedja) {
            this.removeKedjaCommand(kedja);
        }

        kedja.removeKoppling(atgard.guid);

        // Om inte kedjan är identiskt så måste ett command skapas med det som är ändrat
        const kedjaOrginal = this.orginalKedjor.get(kedja.kedjaGuid);
        if (!this.kedjorIdentiska(kedja, kedjaOrginal)) {
            this.updateKedjaCommand(kedja);
        }
    }

    private kedjorIdentiska(kedja: VKAtgardKedja, kedjaOrig: VKAtgardKedja): boolean {
        const originalRowVersions = kedjaOrig.kedjaKopplingar.map(koppling => koppling.rowVersion).sort((a, b) => a.localeCompare(b));
        const currentRowVersions = kedja.kedjaKopplingar.map(koppling => koppling.rowVersion).sort((a, b) => a.localeCompare(b));
        return originalRowVersions.length === currentRowVersions.length && _.isEqual(originalRowVersions, currentRowVersions);
    }

    private getAvtalKontraktsAtgardKedjor(avtalId: number): Observable<IVKAtgardKedjaKopplingResponseDTO[]> {
        return this.api.get<IVKAtgardKedjaKopplingResponseDTO[]>(`virkeskop2/avtal/${avtalId}/atgardskedjor`);
    }

    private getAtgardKedjaInfoForAtgard(atgardId: number): Observable<IVKAtgardKedjaInfoResponseDTO> {
        return this.api.get<IVKAtgardKedjaInfoResponseDTO>(`virkeskop2/atgard/${atgardId}/atgardskedja`);
    }

    private getKedjaKopplingarWithKedjaGuid(kedjaGuid: string): Observable<IVKAtgardKedjaKopplingMasshanteringsResponseDTO[]> {
        return this.api.get<IVKAtgardKedjaKopplingMasshanteringsResponseDTO[]>(`virkeskop2/atgard/${kedjaGuid}/atgardskedja`);
    }

    private getKedjaByAtgardGuid(atgardGuid: string, kedjor: IterableIterator<VKAtgardKedja> = null): VKAtgardKedja {
        if(kedjor === null) {
            kedjor = this.kontraktsKedjor.values();
        }
        for (const kedja of kedjor) {
            if (kedja.containsAtgardGuid(atgardGuid)) {
                return kedja;
            }
        }
        return null;
    }

    private updateKedjaCommand(kedja: VKAtgardKedja) {
        this.kontraktCommandService.update(this.getCommandAtgardKedja(kedja));
        this.kontraktKedjorSrc.next(true);
    }

    private removeKedjaCommand(kedja: VKAtgardKedja) {
        this.kontraktCommandService.remove(this.getCommandAtgardKedja(kedja));
        this.kontraktKedjorSrc.next(true);
    }

    private getCommandAtgardKedja(kedja: VKAtgardKedja): UpdateAtgardKedjaCommand {
        return new UpdateAtgardKedjaCommand(
            kedja.kedjaRowVersion,
            this.kontraktMediatorService.kontrakt.guid,
            kedja.kedjaGuid,
            kedja.kedjaKopplingar);
    }

    private executeCommand(cmd: ICommand): Observable<void> {
        this.commandService.addTransactionCommand(cmd);
        const obs = this.commandService.pushCommandQueueTransaction();

        obs.pipe(tap(x =>  this.commandService.reset()));

        return obs;
    }

}

export interface ICreateExternalKedjeKoppling {
    parentAtgard: Atgard;
    childAtgard: Atgard;
}
